import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getAdicionaisCardapio } from "../../services/api";
import { Modal, Button } from 'react-bootstrap';
import { toast } from "react-toastify";
import { FaCheck } from 'react-icons/fa';
import { MdFastfood } from "react-icons/md";
import SafeImage from "../../components/SafeImage";
import { formatPriceWithSymbol } from '../../utils/currency';
import './modalAdicionaisUnique.css';

const ModalAdicionais = ({ show, onClose, item, idEmpresa, nomeEmpresaForUrl, quantidades, setQuantidades, adicionaisCompletos, setAdicionaisCompletos, handleFinalizarAdicionais }) => {
    const { t } = useTranslation('pdv');

    useEffect(() => {
        if (!item?._id || !idEmpresa) return;
        const fetchAdicionais = async () => {
            try {
                const response = await getAdicionaisCardapio(idEmpresa, nomeEmpresaForUrl);
                const adicionaisFromDB = response.data.adicionais;
                if (item.has_adicional && item.adicionais && adicionaisFromDB) {
                    const adicionaisEncontrados = adicionaisFromDB.filter(adicionalDB =>
                        item.adicionais.some(adicionalItem => adicionalItem.value === adicionalDB._id)
                    );
                    // Tipo A: ordenar os grupos pela ordem definida no item (drag-and-drop)
                    const __ordemGrupos = (item?.adicionais || []).map(a => String(a.value));
                    adicionaisEncontrados.sort((a, b) => __ordemGrupos.indexOf(String(a._id)) - __ordemGrupos.indexOf(String(b._id)));
                    setAdicionaisCompletos(adicionaisEncontrados.map(adicional => ({
                        ...adicional,
                        quantidade: 0
                    })));
                }
            } catch (error) {
                process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao buscar adicionais:', error);
            }
        };
        fetchAdicionais();
    }, [idEmpresa, nomeEmpresaForUrl, item, setAdicionaisCompletos]);

    const handleIncrement = (adicionalPai, idPai, id) => {
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log("adicionalPai:", adicionalPai);
        const calculaMediaOuMaiorValor = adicionalPai.calcular_maior_valor || adicionalPai.calcular_media;
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log("calculaMediaOuMaiorValor:", calculaMediaOuMaiorValor)
        setQuantidades(prev => {
            const maxTotal = adicionaisCompletos.find(adicional => adicional._id === idPai).max;
            // Calcula o total de adicionais já selecionados no grupo (incluindo o adicional atual)
            const totalAtualNoGrupo = Object.keys(prev).reduce((acc, adicionalId) => {
                // Se for o adicional atual, somamos ele também
                acc += prev[adicionalId] || 0;
                return acc;
            }, 0);

            // Se o total de adicionais no grupo já atingiu ou ultrapassou o limite, não permite adicionar mais
            if (totalAtualNoGrupo >= maxTotal) {
                toast(t('addons.maxAllowed'), { autoClose: 1000, type: "warning" });
                return prev; // Não permite adicionar mais adicionais
            }

            // Verifica a quantidade do adicional específico que estamos tentando incrementar
            const totalAtualDoAdicional = prev[id] || 0;

            // Se o adicional tiver 'calcular_maior_valor' ou 'calcular_media', limita a 1
            if (calculaMediaOuMaiorValor && totalAtualDoAdicional >= 1) {
                toast(t('addons.maxAllowed'), { autoClose: 1000, type: "warning" });
                return prev; // Se já houver 1, não permite adicionar mais
            }

            // Para adicionais normais, apenas limita o total no grupo
            if (!calculaMediaOuMaiorValor && totalAtualNoGrupo + 1 > maxTotal) {
                toast(t('addons.maxAllowed'), { autoClose: 1000, type: "warning" });
                return prev; // Se o total no grupo exceder o maxTotal, não permite adicionar mais
            }

            // Se não houver violação de limites, incrementa o adicional
            return { ...prev, [id]: (prev[id] || 0) + 1 };
        });
    };

    const handleDecrement = (id) => {
        setQuantidades(prev => {
            if (prev[id] > 0) {
                return { ...prev, [id]: prev[id] - 1 };
            }
            return prev;
        });
    };

    /*const checkMandatoryAdicionais = () => {
        return adicionaisCompletos.every(grupo => {
            if (!grupo.mandatory) return true;
            const totalSelected = grupo.adicionais.reduce((acc, adicional) => acc + (quantidades[adicional._id] || 0), 0);
            return totalSelected >= grupo.min;
        });
    };*/
    const checkMandatoryAdicionais = () => {
        return adicionaisCompletos.every(grupo => {
            // Se o grupo não for obrigatório, podemos pular ele
            if (!grupo.mandatory) return true;

            // Verificar se há ao menos um subAdicional disponível (out === false)
            const hasValidSubAdicional = grupo.adicionais.some(subAdicional => !subAdicional.out);

            // Se não houver subAdicional disponível, ignore este grupo
            if (!hasValidSubAdicional) return true;

            // Se houver subAdicionais válidos, então fazemos a verificação de quantidade mínima
            const totalSelected = grupo.adicionais.reduce((acc, adicional) => acc + (quantidades[adicional._id] || 0), 0);
            return totalSelected >= grupo.min;
        });
    };

    /*const handleFinalizar = () => {
        if (!checkMandatoryAdicionais()) {
            toast(t('addons.selectAllRequired'), { autoClose: 2000, type: "warning" });
            return;
        }

        const selectedAdicionais = adicionaisCompletos.map(adicional => ({
            grupoId: adicional._id,
            grupoTitle: adicional.title,
            adicionais: adicional.adicionais.map(subAdicional => ({
                adicionalId: subAdicional._id,
                title: subAdicional.title,
                quantity: quantidades[subAdicional._id] || 0,
                price: subAdicional.price
            })).filter(subAdicional => subAdicional.quantity > 0)
        })).filter(adicional => adicional.adicionais.length > 0);

        handleFinalizarAdicionais(item, selectedAdicionais);
    };*/
    const handleFinalizar = () => {
        if (!checkMandatoryAdicionais()) {
            toast(t('addons.selectAllRequired'), { autoClose: 2000, type: "warning" });
            return;
        }

        const selectedAdicionais = adicionaisCompletos.map(adicional => ({
            grupoId: adicional._id,
            grupoTitle: adicional.title,
            calcular_maior_valor: adicional.calcular_maior_valor ?? false, // Inclua o campo calcular_maior_valor, garantindo que seja false se não estiver definido.
            calcular_media: adicional.calcular_media ?? false,
            adicionais: adicional.adicionais.map(subAdicional => ({
                adicionalId: subAdicional._id,
                title: subAdicional.title,
                quantity: quantidades[subAdicional._id] || 0,
                price: subAdicional.price
            })).filter(subAdicional => subAdicional.quantity > 0)
        })).filter(adicional => adicional.adicionais.length > 0);

        handleFinalizarAdicionais(item, selectedAdicionais);
    };

    const isSelected = (adicionalPai) => {
        const totalItems = adicionalPai.adicionais.reduce((acc, adicional) => {
            return acc + (quantidades[adicional._id] || 0);
        }, 0);

        return totalItems >= (adicionalPai.min || 1);
    };

    const isRequirementMet = () => {
        return adicionaisCompletos.every(adicionalPai => {
            if (!adicionalPai.mandatory) {
                // Ignora se não for obrigatório
                return true;
            }

            if (adicionalPai.adicionais.length === 0) {
                // Considera atendido se não há itens adicionais (não deveria ocorrer se for obrigatório e tiver min > 0)
                return true;
            }

            // Calcula a soma total de itens adicionados no grupo obrigatório
            const totalItems = adicionalPai.adicionais.reduce((acc, adicional) => {
                return acc + (quantidades[adicional._id] || 0);
            }, 0);

            // Verifica se o total atende ao mínimo requerido, se houver um mínimo definido
            return adicionalPai.min > 0 ? totalItems >= adicionalPai.min : true;
        });
    };

    useEffect(() => {
        isRequirementMet();
    }, [quantidades]);  // Dependendo de quantidades para reavaliar quando mudar


    return (
        <Modal show={show} onHide={onClose} dialogClassName="modal-adicional-unique" enforceFocus={false} restoreFocus={false}>

            <Modal.Header>
                <Modal.Title>{item.title}</Modal.Title>
                <button onClick={onClose} style={{
                    background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8,
                    width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: 'white', fontSize: 18, fontWeight: 300, lineHeight: 1
                }}>✕</button>
            </Modal.Header>
            <Modal.Body>
                {adicionaisCompletos?.map((adicional, index) => (
                    <div key={adicional._id}>
                        <div className="adicional-group-unique">
                            {adicional.adicionais.some(subAdicional => !subAdicional.out) && (
                                <>
                                    <div className='line-separator-unique' style={{ marginTop: 10 }}></div>
                                    <div className='adicional-title-container-unique'>
                                        <span className='adicional-title-text-unique'>{adicional.title}</span>
                                        {adicional.mandatory && (
                                            <>
                                                <label className={`adicional-required-label-unique ${isSelected(adicional) ? "adicional-hidden-unique" : "adicional-visible-unique"}`}>{t('addons.required')}</label>
                                                <FaCheck className={`adicional-check-icon-unique ${isSelected(adicional) ? "adicional-visible-unique" : "adicional-hidden-unique"}`} />
                                            </>
                                        )}
                                    </div>
                                    <div style={{marginBottom:10}}>
                                        <span className='adicional-description-text-unique'>
                                        {adicional.mandatory && adicional.min > 1
                                            ? t('addons.chooseMinUpTo', { min: adicional.min, count: adicional.max })
                                            : t('addons.chooseUpTo', { count: adicional.max })}
                                    </span>
                                    </div>
                                </>
                            )
                            }
                            <div className='adicional-items-container-unique'>
                                {adicional.adicionais.map(subAdicional => {
                                    if (!subAdicional.out) {

                                        return (
                                            <div key={subAdicional._id} className={'adicional-item-card-unique'}>
                                                <div className="adicional-item-image-unique">
                                                    <SafeImage
                                                        width={100}
                                                        height={75}
                                                        src={subAdicional.image}
                                                        alt={subAdicional.title}
                                                        fallback={
                                                            <div className="adicional-item-icon-placeholder-unique">
                                                                <MdFastfood style={{ fontSize: 75 }} />
                                                            </div>
                                                        }
                                                    />
                                                </div>
                                                <div className="adicional-item-name-unique">
                                                    <span className="adicional-item-title-unique">{subAdicional.title}</span>
                                                </div>

                                                <div className="adicional-item-controls-unique">
                                                    <div className="adicional-item-price-unique">
                                                        {formatPriceWithSymbol(subAdicional.price)}
                                                    </div>
                                                    <div className="adicional-quantity-input-unique">
                                                        <div className="adicional-quantity-controls-unique">
                                                            <button className="adicional-qty-btn-unique"
                                                                onClick={() => handleDecrement(subAdicional._id)}
                                                                type="button"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#2b2b2b' }}><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                                                            </button>
                                                            <input className="adicional-qty-input-unique" type="number" value={quantidades[subAdicional._id] || 0} readOnly />
                                                            <button className="adicional-qty-btn-unique"
                                                                onClick={() => handleIncrement(adicional, adicional._id, subAdicional._id)}
                                                                type="button"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#2b2b2b' }}><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                            </div>
                                        );
                                    }
                                    return null; // Garante que nada será renderizado se subAdicional.out for true
                                })}

                            </div>
                        </div>
                        {/*index !== adicionaisCompletos.length - 1 && (
                            <div className='line' style={{ marginTop: 10 }}></div>
                        )*/}
                    </div>
                ))}
            </Modal.Body>
            <Modal.Footer>
                <Button className="adicional-btn-unique adicional-btn-secondary-unique" onClick={onClose}>
                    {t('addons.close')}
                </Button>
                <Button className="adicional-btn-unique adicional-btn-primary-unique" onClick={handleFinalizar}>
                    {t('addons.includeAddons')}
                </Button>
            </Modal.Footer>

        </Modal >
    );
};

export default ModalAdicionais;

